import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import {
  Box,
  Button,
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import getSymbolFromCurrency from 'currency-symbol-map';
import React, { memo, useState } from 'react';
import AxiosConfig from '../../../../config/AxiosConfig';
import b64toBlob from '../../../helpers/utils/blob';
import BrandIcon from '../../utils/common/BrandIcon';
import PaymentMethodIcon from '../../utils/common/PaymentMethodIcon';
import { ActionButton, RefundStatusIcon } from './Utils/Utils.jsx';

const CopyableText = ({ text, tooltipText }) => {
  const [tooltip, setTooltip] = useState('');

  const handleCopy = (e) => {
    e.stopPropagation();
    if (text) {
      navigator.clipboard.writeText(text);
      setTooltip('Copié !');
      setTimeout(() => setTooltip(''), 2000);
    }
  };

  return (
    <Tooltip title={tooltip || tooltipText} arrow placement="right">
      <Typography onClick={handleCopy} sx={{ cursor: 'pointer', fontSize: 14 }}>
        {text || '--'}
      </Typography>
    </Tooltip>
  );
};

const formatDate = (date) => {
  return date
    ? new Date(date).toLocaleString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
    : '--';
};

const getRefundStatusTooltip = (refund) => {
  if (
    refund?.status?.code === 'PARTIAL_REFUND_PENDING' ||
    refund?.status?.code === 'REFUND_PENDING'
  ) {
    return 'En attente de traitement par les plateformes de paiement';
  }
  if (refund?.status?.code === 'REFUND_FAILED_BO') {
    return 'Refusé par la plateforme de paiement';
  }
  if (refund?.status?.code === 'PARTIAL_REFUND_FAILED') {
    return 'Refusé par la plateforme de paiement';
  }
  return refund?.refusedDescription || '--';
};

const DashboardFiRow = memo(({ refund, profile, isCurrentRefund }) => {
  const [tooltipEmail, setTooltipEmail] = useState('');

  const handleDownloadRIB = async () => {
    try {
      await AxiosConfig.create('get', `/refunds/${refund?._id}/downloadRIB`)
        .call()
        .exit((res) => {
          const content = res.data;
          if (content?.length > 0) {
            const blobContent = b64toBlob(
              content?.split(',')[1]?.replace(/_/g, '/')?.replace(/-/g, '+'),
              refund?.rib?.mimeType
            );
            const blobUrl = URL.createObjectURL(blobContent);
            const link = document.createElement('a');
            link.setAttribute(
              'download',
              `rib_${refund.order.contact.reference}_${refund.order.reference}_${refund?.rib?.name}`
            );
            link.href = blobUrl;
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
          }
        });
    } catch (error) {
      console.error('Erreur lors du téléchargement du RIB :', error);
    }
  };

  const currencySymbol = getSymbolFromCurrency(
    refund?.ticket?.contact?.store.country?.currency?.code
  );

  return (
    <TableRow key={refund?._id}>
      <TableCell align="center" sx={{ fontSize: 14 }}>
        {formatDate(refund?.createdAt)}
      </TableCell>

      <TableCell align="center" sx={{ fontSize: 14 }}>
        {refund?.ticket?.reference || '--'}
      </TableCell>

      <TableCell align="center" sx={{ fontSize: 14 }}>
        <BrandIcon
          brand={refund?.order?.contact?.store.brand.name}
          type={refund?.order?.contact?.store.contactType?.codeBO}
          sx={{ fontSize: 26 }}
        />
        <CopyableText
          text={refund?.order?.reference}
          tooltipText={`Cliquez pour copier la ref: ${refund?.order?.reference}`}
        />
        <CopyableText
          text={refund?.order?.mpId}
          tooltipText={`Cliquez pour copier la ref: ${refund?.order?.mpId}`}
        />
      </TableCell>

      <TableCell align="center" sx={{ fontSize: 14 }}>
        <Box display="flex" justifyContent="center" alignItems="center">
          <PaymentMethodIcon
            paymentmethod={refund?.paymentMethod}
            style={{ fontSize: 64 }}
          />
        </Box>
      </TableCell>

      <TableCell align="center" sx={{ fontSize: 14 }}>
        <Typography variant="body2">
          {refund?.firstName} {refund?.lastName}
        </Typography>
        <Tooltip title={tooltipEmail} arrow>
          <IconButton
            onClick={() => {
              navigator.clipboard.writeText(refund?.email);
              setTooltipEmail('Email copié !');
              setTimeout(() => setTooltipEmail(''), 2000);
            }}
            onMouseEnter={() =>
              setTooltipEmail(`Cliquer pour copier l'email : ${refund?.email}`)
            }
            onMouseLeave={() => setTooltipEmail('')}
            sx={{ mt: 1 }}
          >
            <EmailIcon />
          </IconButton>
        </Tooltip>
      </TableCell>

      <TableCell align="center" sx={{ fontSize: 14 }}>
        <Tooltip title={refund?.description || '--'} arrow>
          <Typography>{refund?.refundType?.name || '--'}</Typography>
        </Tooltip>
      </TableCell>

      <TableCell align="center" sx={{ fontSize: 14 }}>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            A Rembourser : {refund?.amountToRefund || '--'}
            {currencySymbol}
          </Typography>
          <Typography variant="body2" sx={{ color: 'gray' }}>
            Total : {refund?.amountTotal || '--'}
            {currencySymbol}
          </Typography>
          {refund?.rib && (
            <Button
              color="primary"
              variant="outlined"
              size="small"
              onClick={handleDownloadRIB}
              endIcon={<DownloadIcon />}
            >
              RIB
            </Button>
          )}
        </Box>
      </TableCell>

      <TableCell align="center" sx={{ fontSize: 14 }}>
        <Tooltip
          title={`Niveau de permission: ${refund?.author?.permission?.level || 'N/A'}`}
          arrow
        >
          <Typography sx={{ fontSize: 12 }}>
            {refund?.author?.firstName || '--'}{' '}
            {refund?.author?.lastName || '--'}
          </Typography>
        </Tooltip>
      </TableCell>

      <TableCell align="center" sx={{ fontSize: 14 }}>
        <Tooltip
          title={getRefundStatusTooltip(refund)}
          arrow
          sx={{ cursor: 'help' }}
        >
          <Box component="span">
            <RefundStatusIcon status={refund?.status} sx={{ fontSize: 14 }} />
          </Box>
        </Tooltip>
      </TableCell>

      {isCurrentRefund ? (
        <TableCell align="center" sx={{ width: 260, height: 50 }}>
          {(refund?.status?.code === 'TOVALIDATEFI' ||
            refund?.status?.code === 'REFUNDFAILED' ||
            refund?.status?.code === 'WAITINGFI') &&
          profile ? (
            <ActionButton refund={refund} profile={profile} />
          ) : (
            '--'
          )}
        </TableCell>
      ) : (
        <TableCell align="center" sx={{ fontSize: 14 }}>
          {formatDate(refund?.refundedAt)}
        </TableCell>
      )}
    </TableRow>
  );
});

export default DashboardFiRow;
